import React from 'react';
import {
  Typography,
  Button,
  makeStyles,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Snackbar,
  SnackbarContent,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  checkEmailMessage: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  verificationCodeInput: {
    textAlign: 'center',
    '& input': {
      textAlign: 'center',
      fontSize: '24px',
      letterSpacing: '8px',
    },
  },
}));

const EmailChangeModal = ({
  open,
  onClose,
  currentEmail,
  onSave,
  onVerifyCode,
}) => {
  const classes = useStyles();
  const [newEmail, setNewEmail] = React.useState('');
  const [verificationCode, setVerificationCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [codeSent, setCodeSent] = React.useState(false);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setCodeSent(false);
      setNewEmail('');
      setVerificationCode('');
      setError('');
      setSuccess('');
      setLoading(false);
    }
  }, [open]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await onSave(newEmail, false);
      if (result.success) {
        setSuccess(
          'Verification code sent! Please check your new email address.'
        );
        setCodeSent(true);
        setOpenSnackbar(true);
      } else {
        setError(result.error.message || 'Failed to send email change request');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  const handleClose = () => {
    setNewEmail('');
    setVerificationCode('');
    setCodeSent(false);
    setError('');
    setSuccess('');
    setLoading(false);
    onClose();
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await onSave(newEmail, false);
      if (result.success) {
        setSuccess(
          'Verification code resent! Please check your new email address.'
        );
        setOpenSnackbar(true);
      } else {
        setError(result.message || 'Failed to resend verification code');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to resend verification code');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await onVerifyCode(verificationCode);
      if (result.success) {
        setSuccess('Email address updated successfully!');
        setOpenSnackbar(true);
        // Close modal after successful verification
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(result.message || 'Invalid verification code');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to verify code');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = (e) => {
    e.preventDefault();
    handleVerifyCode();
  };

  const handleBackToEmail = () => {
    setCodeSent(false);
    setNewEmail('');
    setVerificationCode('');
    setError('');
    setSuccess('');
  };

  // Show verification code input after code is sent
  if (codeSent) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Verification Code</DialogTitle>
        <form onSubmit={handleVerifySubmit}>
          <DialogContent>
            <div className={classes.checkEmailMessage}>
              <Typography variant="body1" gutterBottom>
                We've sent a verification code to <strong>{newEmail}</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                Enter the 6-digit code to complete the email address change.
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                If you don't see the email, check your spam folder.
              </Typography>
              <Box mt={3}>
                <TextField
                  autoFocus
                  label="Verification Code"
                  type="text"
                  fullWidth
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, '').slice(0, 6)
                    )
                  }
                  error={!!error}
                  helperText={error}
                  className={classes.verificationCodeInput}
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                  }}
                />
              </Box>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleBackToEmail} color="primary">
              Back
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleResendVerification}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Resend Code'}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Code'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Email Address</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Current Email: {currentEmail}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter your new email address. We'll send a verification code to
            confirm the change.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Email Address"
            type="email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={loading || !newEmail.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Request Email Change'}
          </Button>
        </DialogActions>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <SnackbarContent
          className={error ? classes.error : classes.success}
          message={<span className={classes.message}>{error || success}</span>}
        />
      </Snackbar>
    </Dialog>
  );
};

export default EmailChangeModal;
